import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    TicketIcon,
    EyeIcon,
    EllipsisVerticalIcon,
} from '@heroicons/react/20/solid'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { WrenchScrewdriverIcon } from '@heroicons/react/16/solid'
import { Button } from '../../components/button'
import { Input, InputGroup } from '../../components/input'
import { Select } from '../../components/select'
import { Badge } from '../../components/badge'
import { Divider } from '../../components/divider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../../components/dropdown'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from '../../components/custom/ResponseDialog'
import {
    Pagination,
    PaginationList,
    PaginationNext,
    PaginationPage,
    PaginationPrevious,
} from '../../components/pagination'
import { getPageRange } from '../../utils/paginationHelper'
import loadingImage from '../../assets/images/boat-loading.gif'
import { GET_LOCATIONS, GET_STATION_DISTANCE_CONFIGS, STATION_DISTANCE_STATUS } from 'graphql/stationDistanceConfig/queries'
import { UPDATE_STATION_DISTANCE_CONFIG } from 'graphql/stationDistanceConfig/mutations'
import { formatDateOnly } from 'utils/common'

const StationDistanceConfig = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [statusFilter, setStatusFilter] = useState('')
    const [stationFromFilter, setStationFromFilter] = useState('')
    const [stationToFilter, setStationToFilter] = useState('')
    const [activateDialog, setActivateDialog] = useState({ open: false, id: null })
    const [deactivateDialog, setDeactivateDialog] = useState({ open: false, id: null })
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '' })
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [locations, setLocations] = useState([]);
    const [statusCodes, setStatusCodes] = useState([]);
    const [sdcs, setSdcs] = useState({});

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const { data: allLocations } = useQuery(GET_LOCATIONS, {
        variables: {
            page: { limit: 100, offset: 0 }
        },
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.searchLocation?.locations) {
                setLocations(data.searchLocation.locations);
            }
        },
    })

    const { data: fetchedData, loading: loading, error: error, refetch: refetch } = useQuery(GET_STATION_DISTANCE_CONFIGS, {
        variables: {
            filter: { 
                from_station_id: stationFromFilter === '' ? undefined : stationFromFilter,
                to_station_id: stationToFilter === '' ? undefined : stationToFilter,
                status_code: statusFilter === '' ? undefined : statusFilter
            },
            page: {
                offset: page * rowsPerPage,
                limit: rowsPerPage,
            },
        },
        fetchPolicy: 'network-only',
        onCompleted: data => {
            setSdcs(data);
        },
    })

    if (sdcs !== fetchedData) {
        setSdcs(fetchedData);
    }

    useQuery(STATION_DISTANCE_STATUS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.stationDistanceStatus) {
                setStatusCodes(data.stationDistanceStatus);
            }
        },
    })

    // Update Station distance configuration Status
    const [updateStationDistanceConfigStatus, { loading: activateLoading }] = useMutation(UPDATE_STATION_DISTANCE_CONFIG, {
        onCompleted: data => {
            if (data?.updateStationDistanceConfig?.success) {
                setSuccessDialog({
                    open: true,
                    message: data.updateStationDistanceConfig.message || 'Station distance config activated successfully',
                })
                setShowSucDialog(true)
                refetch()
            } else {
                setErrorDialog({
                    open: true,
                    message: data?.updateStationDistanceConfig?.message || 'Failed to activate Station distance config',
                })
                setShowErrDialog(true)
            }
            setActivateDialog({ open: false, id: null, name: '' })
            setDeactivateDialog({ open: false, id: null, name: '' })
        },
        onError: error => {
            setErrorDialog({
                open: true,
                message: error.message || 'An error occurred while activating Station distance config',
            })
            setShowErrDialog(true)
            setActivateDialog({ open: false, id: null, name: '' })
            setDeactivateDialog({ open: false, id: null, name: '' })
        },
    })

    
    const stationDistanceConfigs = sdcs?.getStationDistanceConfigs?.station_distance_configs ?? [];
    const totalCount = sdcs?.getStationDistanceConfigs?.total_count ?? 0;
    const totalPages = Math.ceil(totalCount / rowsPerPage);

    const filteredStationDistanceConfigs = stationDistanceConfigs.filter(sdc => {
        
        const matchesStatus = !statusFilter || sdc.status_code === statusFilter
        
        const matchStationFrom = !stationFromFilter || sdc.from_station_id === stationFromFilter
        
        const matchStationTo = !stationToFilter || sdc.to_station_id === stationToFilter
        
        return matchesStatus && matchStationFrom && matchStationTo
    }).sort((a, b)=> { return a.station_distance_id.localeCompare(b.station_distance_id) });

    const onClickClear = () => {
        setStatusFilter('')
        setStationFromFilter('')
        setStationToFilter('')
        setPage(0)
    }

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const getCategoryBadge = categoryCode => {
        const categoryMap = {
            DMC: { color: 'blue', label: 'Domestic' },
            INL: { color: 'purple', label: 'International' },
        }
        const category = categoryMap[categoryCode] || { color: 'zinc', label: categoryCode }
        return <Badge color={category.color}>{t(category.label)}</Badge>
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessDialog({ open: false, message: '' })
        setShowErrDialog(false)
        setErrorDialog({ open: false, message: '' })
    }

    const handleActivate = (id) => {
        setActivateDialog({
            open: true,
            id
        })
    }

    const handleDeactivate = (id, name) => {
        setDeactivateDialog({
            open: true,
            id
        })
    }

    const confirmActivate = () => {
        if (activateDialog.id) {
            const sdcFilt = stationDistanceConfigs.filter((v)=>{return v.station_distance_id === activateDialog.id});
            if (sdcFilt.length > 0) {
                const sdcInQuestion = sdcFilt[0];
                if (sdcInQuestion.status_code === 'INA') {
                    const v = updateStationDistanceConfigStatus({
                        variables: {
                            station_distance_id: sdcInQuestion.station_distance_id,
                            station_distance_config: {
                                from_station_id: sdcInQuestion.from_station_id,
                                to_station_id: sdcInQuestion.to_station_id,
                                distance_nm: sdcInQuestion.distance_nm,
                                is_bidirectional: sdcInQuestion.is_bidirectional,
                                effective_from: sdcInQuestion.effective_from,
                                effective_to: sdcInQuestion.effective_to,
                                status_code: 'ACT',
                            },
                        }
                    });
                }
            }
        }
    }

    const confirmDeactivate = () => {
        if (deactivateDialog.id) {
            const sdcFilt = stationDistanceConfigs.filter((v)=>{return v.station_distance_id === deactivateDialog.id});
            if (sdcFilt.length > 0) {
                const sdcInQuestion = sdcFilt[0];
                if (sdcInQuestion.status_code === 'ACT') {
                    const v = updateStationDistanceConfigStatus({
                        variables: {
                            station_distance_id: sdcInQuestion.station_distance_id,
                            station_distance_config: {
                                from_station_id: sdcInQuestion.from_station_id,
                                to_station_id: sdcInQuestion.to_station_id,
                                distance_nm: sdcInQuestion.distance_nm,
                                is_bidirectional: sdcInQuestion.is_bidirectional,
                                effective_from: sdcInQuestion.effective_from,
                                effective_to: sdcInQuestion.effective_to,
                                status_code: 'INA',
                            },
                        }
                    });
                }
            }
        }
    }
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <WrenchScrewdriverIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage Station Distance Configurations')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit Station Distances between stations.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/nautical-miles-distance/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Station Distance Config')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 mb-4 flex flex-col sm:flex-row flex-wrap gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Status')}</label>
                            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="">{t('All Statuses')}</option>
                                {statusCodes.map((v)=>(<option value={v.code}>{t(v.description.en ?? v.code)}</option>))}
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Station From')}</label>
                            <Select value={stationFromFilter} onChange={e => setStationFromFilter(e.target.value)}>
                                <option value="">{t('Any Station')}</option>
                                {locations.map((v)=>(<option value={v.id}>{t(v.name.en ?? v.id)}</option>))}
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Station To')}</label>
                            <Select value={stationToFilter} onChange={e => setStationToFilter(e.target.value)}>
                                <option value="">{t('Any Station')}</option>
                                {locations.map((v)=>(<option value={v.id}>{t(v.name.en ?? v.id)}</option>))}
                            </Select>
                        </div>
                        <button
                            onClick={onClickClear}
                            type="button"
                            style={{height: '50%', alignSelf: 'end'}}
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Clear')}
                        </button>
                    </div>
                </div>
            </div>
            <Divider />
            {loading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {error && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch Station Distance Configs!')}</div>
                </div>
            )}

            {!loading && !error && filteredStationDistanceConfigs.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!loading && !error && filteredStationDistanceConfigs.length > 0 && (
                <>
                    <div className="table-custom rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)] w-full">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('From country')}</TableHeader>
                                    <TableHeader>{t('From location')}</TableHeader>
                                    <TableHeader>{t('To country')}</TableHeader>
                                    <TableHeader>{t('To location')}</TableHeader>
                                    <TableHeader>{t('Distance (NM)')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Effective from')}</TableHeader>
                                    <TableHeader>{t('Effective to')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredStationDistanceConfigs.map(sdc => (
                                    <TableRow
                                        key={sdc.station_distance_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/nautical-miles-distance/edit/${sdc.station_distance_id}`)}
                                        title={`config #${sdc.station_distance_id}`}
                                    >
                                        <TableCell className="min-w-[100px]">
                                            <span className="font-semibold text-primary">
                                                {(()=>{
                                                    const locFilter = locations.filter((v)=>{return v.id === sdc.from_station_id});
                                                    return locFilter.length > 0 ? locFilter[0].country_details.country : ""
                                                })()}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            <span className="font-semibold text-primary">
                                                {sdc.from_station.name.en}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            <span className="font-semibold text-primary">
                                                {(()=>{
                                                    const locFilter = locations.filter((v)=>{return v.id === sdc.to_station_id});
                                                    return locFilter.length > 0 ? locFilter[0].country_details.country : ""
                                                })()}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            <span className="font-semibold text-primary">
                                                {sdc.to_station.name.en}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {sdc.distance_nm}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {t(sdc.status.description.en)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {sdc.effective_from}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {sdc.effective_to}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/nautical-miles-distance/edit/${sdc.station_distance_id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/nautical-miles-distance/edit/${sdc.station_distance_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    {sdc.status_code === 'INA' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleActivate(
                                                                        sdc.station_distance_id
                                                                    )
                                                                }}
                                                            >
                                                                <PlusIcon />
                                                                <DropdownLabel>{t('Activate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {sdc.status_code === 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDeactivate(
                                                                        sdc.station_distance_id
                                                                    )
                                                                }}
                                                            >
                                                                <XMarkIcon />
                                                                <DropdownLabel>{t('Deactivate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    {totalPages > 1 && (
                        <Pagination className="py-6">
                            <PaginationPrevious
                                onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                                isDisabled={page === 0}
                            />
                            <PaginationList>
                                {getPageRange(page, totalPages).map(index => (
                                    <PaginationPage
                                        key={index}
                                        current={index === page}
                                        onClick={() => handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </PaginationPage>
                                ))}
                            </PaginationList>
                            <PaginationNext
                                onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                                isDisabled={page === totalPages - 1}
                            />
                        </Pagination>
                    )}
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={successDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {activateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Activate Station distance Configuration')}
                    message={`${t('Are you sure you want to activate the configuration')}?`}
                    onPositive={confirmActivate}
                    onNegative={() => setActivateDialog({ open: false, id: null })}
                    positiveActionText={activateLoading ? t('Activating...') : t('Activate')}
                    negativeActionText={t('Cancel')}
                />
            )}
            {deactivateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Deactivate Station distance Configuration')}
                    message={`${t('Are you sure you want to deactivate the configuration')}?`}
                    onPositive={confirmDeactivate}
                    onNegative={() => setDeactivateDialog({ open: false, id: null })}
                    positiveActionText={activateLoading ? t('Deactivating...') : t('Deactivate')}
                    negativeActionText={t('Cancel')}
                />
            )}
        </>
    )
}

export default StationDistanceConfig